import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Button,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  SportsSoccer as MatchIcon,
  AccessTime as TimeIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  OpenInFull as ExpandIcon,
  PlayArrow as PlayIcon,
  AttachMoney as BudgetIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
  GameweekStatusBadge,
  EditGameweekModal,
} from "../components/gameweeks";
import { get, del, patch } from "../utils/networkUtils";
import { Link as RouterLink } from "react-router-dom";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`gameweek-tabpanel-${index}`}
    aria-labelledby={`gameweek-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const GameweekDetailPage = () => {
  const { id: urlTournamentId, gameweekId } = useParams();
  const navigate = useNavigate();

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const tournamentId = uuidRegex.test(urlTournamentId)
    ? urlTournamentId
    : urlTournamentId;

  const [tournament, setTournament] = useState(null);
  const [gameweek, setGameweek] = useState(null);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchGameweekData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await get(`/admin/gameweek/${gameweekId}`);

      if (response?.data?.gameweek) {
        const gw = response.data.gameweek;
        const matches = response.data.matches || [];

        setGameweek({
          id: gw.id,
          tournamentId: gw.tournamentCalendarId,
          number: gw.number,
          name: gw.name,
          assignedMatches: gw.assignedMatches || 0,
          status: gw.status,
          startsAt: gw.startsAt,
          endsAt: gw.endsAt,
          deadlineAt: gw.deadlineAt,
          budgetInitial: gw.budgetInitial,
          isDoubleGameweek: gw.isDoubleGameweek,
          knockoutRound: gw.knockoutRound,
          createdAt: gw.createdAt,
          updatedAt: gw.updatedAt,
        });

        const mappedMatches = matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          gameweek: m.gameweek,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || "TBD",
          awayContestantName: m.awayContestantName || "TBD",
        }));

        setAssignedMatches(mappedMatches);

        const tResponse = await get(`/admin/tournament-calendar/${tournamentId}`);
        if (tResponse?.data) {
          setTournament(tResponse.data);
        }
      } else {
        setGameweek(null);
      }
    } catch (error) {
      console.error("Failed to fetch gameweek:", error);
      setSnackbar({
        open: true,
        message: "Failed to load gameweek data",
        severity: "error",
      });
      setGameweek(null);
    } finally {
      setLoading(false);
    }
  }, [gameweekId, tournamentId]);

  useEffect(() => {
    fetchGameweekData();
  }, [fetchGameweekData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await patch(`/admin/gameweek/${gameweekId}/status`, {
        status: newStatus,
      });

      setGameweek((prev) => ({ ...prev, status: newStatus }));
      setSnackbar({
        open: true,
        message: `Gameweek status changed to ${newStatus}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        status: formData.status,
        budgetInitial: formData.budgetInitial,
        isDoubleGameweek: formData.isDoubleGameweek,
        deadlineAt: formData.deadlineAt,
      };

      const response = await patch(`/admin/gameweek/${gameweekId}`, payload);

      if (response?.data) {
        setGameweek((prev) => ({
          ...prev,
          name: response.data.name,
          status: response.data.status,
          budgetInitial: response.data.budgetInitial,
          isDoubleGameweek: response.data.isDoubleGameweek,
          deadlineAt: response.data.deadlineAt,
          updatedAt: response.data.updatedAt,
        }));
        setSnackbar({
          open: true,
          message: `Gameweek "${formData.name}" updated successfully`,
          severity: "success",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Failed to update gameweek:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update gameweek",
        severity: "error",
      });
      return false;
    }
  };

  const handleDelete = async () => {
    if (assignedMatches.length > 0) {
      setSnackbar({
        open: true,
        message: "Cannot delete gameweek with assigned matches",
        severity: "error",
      });
      setDeleteDialog(false);
      return;
    }

    try {
      await del(`/admin/gameweek/${gameweekId}`);

      setSnackbar({
        open: true,
        message: "Gameweek deleted successfully",
        severity: "success",
      });
      setDeleteDialog(false);
      setTimeout(() => {
        navigate(`/dashboard/tournaments/${urlTournamentId}/gameweeks`);
      }, 1500);
    } catch (error) {
      console.error("Failed to delete gameweek:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to delete gameweek",
        severity: "error",
      });
      setDeleteDialog(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return format(new Date(dateString), "MMMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!gameweek) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Gameweek not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{gameweek.name} | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${urlTournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament?.competition_name}
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${urlTournamentId}/gameweeks`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Gameweeks
          </Typography>
          <Typography color="text.primary">{gameweek.name}</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                {gameweek.name}
              </Typography>
              <GameweekStatusBadge status={gameweek.status} />
              {gameweek.isDoubleGameweek && (
                <Chip label="Double Gameweek" color="primary" size="small" />
              )}
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              {tournament?.competition_name} • Gameweek #{gameweek.number}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() =>
                navigate(`/dashboard/tournaments/${urlTournamentId}/gameweeks`)
              }
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditModalOpen(true)}
            >
              Edit
            </Button>
            {gameweek.status === "upcoming" && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialog(true)}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>

        <Card>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 2 }}
          >
            <Tab label="Overview" />
            <Tab label={`Matches (${assignedMatches.length})`} />
            <Tab label="Assign Matches" />
          </Tabs>

          <CardContent>
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Gameweek Details
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Gameweek Number"
                        secondary={gameweek.number}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText primary="Name" secondary={gameweek.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Assigned Matches"
                        secondary={`${assignedMatches.length} matches`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BudgetIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Initial Budget"
                        secondary={
                          gameweek.budgetInitial != null
                            ? `£${(gameweek.budgetInitial / 1_000_000).toFixed(
                                1
                              )}m`
                            : "Not set"
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Schedule
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Starts At"
                        secondary={formatDate(gameweek.startsAt)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Ends At"
                        secondary={formatDate(gameweek.endsAt)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Deadline At (Transfer Deadline)"
                        secondary={formatDate(gameweek.deadlineAt)}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Status Actions
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {gameweek.status === "upcoming" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayIcon />}
                    onClick={() => handleStatusChange("open")}
                  >
                    Open Gameweek
                  </Button>
                )}
                {gameweek.status === "open" && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<LockIcon />}
                    onClick={() => handleStatusChange("locked")}
                  >
                    Lock Gameweek
                  </Button>
                )}
                {gameweek.status === "locked" && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<PlayIcon />}
                    onClick={() => handleStatusChange("live")}
                  >
                    Go Live
                  </Button>
                )}
                {gameweek.status === "live" && (
                  <Button
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={() => handleStatusChange("finalised")}
                  >
                    Finalise Gameweek
                  </Button>
                )}
                {gameweek.status === "finalised" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ReplayIcon />}
                    onClick={() => handleStatusChange("upcoming")}
                  >
                    Reopen Gameweek
                  </Button>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Assigned Matches
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ExpandIcon />}
                  onClick={() =>
                    navigate(
                      `/dashboard/tournaments/${urlTournamentId}/gameweek-builder?gameweek=${gameweekId}`
                    )
                  }
                >
                  Open in Builder
                </Button>
              </Box>

              {assignedMatches.length === 0 ? (
                <Alert severity="info">
                  No matches assigned yet. Go to the "Assign Matches" tab to add
                  matches.
                </Alert>
              ) : (
                <List>
                  {assignedMatches.map((match, index) => (
                    <React.Fragment key={match.id}>
                      <ListItem>
                        <ListItemIcon>
                          <Typography fontWeight={700} color="primary">
                            {index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography fontWeight={600}>
                                {match.homeContestantName}
                              </Typography>
                              <Typography color="text.secondary">vs</Typography>
                              <Typography fontWeight={600}>
                                {match.awayContestantName}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatDate(match.kickoffAt)}
                              </Typography>
                              {match.gameweek && (
                                <Chip
                                  label={`GW ${match.gameweek}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                              <Chip
                                label={
                                  match.status === "fixture"
                                    ? "Upcoming"
                                    : match.status
                                }
                                size="small"
                                color={
                                  match.status === "played"
                                    ? "success"
                                    : "default"
                                }
                              />
                              {match.status === "played" && (
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color="primary"
                                >
                                  {match.homeScore} - {match.awayScore}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < assignedMatches.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Assign Matches to {gameweek.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Use the Gameweek Builder to assign matches to this gameweek
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ExpandIcon />}
                  onClick={() =>
                    navigate(
                      `/dashboard/tournaments/${urlTournamentId}/gameweek-builder?gameweek=${gameweekId}`
                    )
                  }
                >
                  Open Gameweek Builder
                </Button>
              </Box>
            </TabPanel>
          </CardContent>
        </Card>
      </Container>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Gameweek?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{gameweek.name}"?
          </Typography>
          {assignedMatches.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This gameweek has {assignedMatches.length} assigned match(es).
              Remove all matches first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={assignedMatches.length > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <EditGameweekModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        gameweek={gameweek}
        onSubmit={handleEditSubmit}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default GameweekDetailPage;
